import { useCallback, useEffect, useState, useRef } from 'react'
import type { ColumnConfig } from '../components/ColumnSelector'

/**
 * Hook for managing column visibility state with localStorage persistence.
 * Uses lazy initialization to read from localStorage before first render.
 *
 * @param storageKey - localStorage key for persisting column state
 * @param defaultColumns - Default column configuration
 * @returns [columns, toggleColumn, isVisible]
 */
export function useColumnVisibility(
    storageKey: string,
    defaultColumns: ColumnConfig[],
): [ColumnConfig[], (id: string) => void, (id: string) => boolean] {
    // Lazy initialization - reads from localStorage before first render
    const [columns, setColumns] = useState<ColumnConfig[]>(() => {
        if (typeof window === 'undefined') return defaultColumns

        try {
            const saved = localStorage.getItem(storageKey)
            if (saved) {
                const parsed = JSON.parse(saved)
                return defaultColumns.map((col) => {
                    const savedCol = parsed.find((s: any) => s.id === col.id)
                    return savedCol ? { ...col, visible: savedCol.visible } : col
                })
            }
        } catch (e) {
            console.warn(`[useColumnVisibility] Failed to load from ${storageKey}:`, e)
        }

        return defaultColumns
    })

    // Track the current storage key to detect changes
    const prevKeyRef = useRef(storageKey)

    // Sync from localStorage if the storageKey prop changes dynamically
    useEffect(() => {
        if (prevKeyRef.current !== storageKey) {
            prevKeyRef.current = storageKey
            if (typeof window !== 'undefined') {
                try {
                    const saved = localStorage.getItem(storageKey)
                    if (saved) {
                        const parsed = JSON.parse(saved)
                        setColumns(defaultColumns.map((col) => {
                            const savedCol = parsed.find((s: any) => s.id === col.id)
                            return savedCol ? { ...col, visible: savedCol.visible } : col
                        }))
                    } else {
                        setColumns(defaultColumns)
                    }
                } catch (e) { console.warn('Ignored error in useColumnVisibility.ts:', e); }
            }
        }
    }, [storageKey, defaultColumns])

    // Persist to localStorage when columns change
    useEffect(() => {
        // Prevent writing old state to new key during the exact frame the key changes
        if (prevKeyRef.current !== storageKey) return;
        
        if (typeof window !== 'undefined') {
            try {
                const serialized = JSON.stringify(columns)
                const current = localStorage.getItem(storageKey)
                if (current !== serialized) {
                    localStorage.setItem(storageKey, serialized)
                    window.dispatchEvent(new CustomEvent('columnVisibilitySync', { detail: { storageKey, columns } }))
                }
            } catch (e) {
                console.warn(`[useColumnVisibility] Failed to save to ${storageKey}:`, e)
            }
        }
    }, [columns, storageKey])

    // Listen for cross-component sync
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleSync = (e: any) => {
            if (e.detail && e.detail.storageKey === storageKey) {
                // Check if they are actually different to prevent infinite loops
                const currentSerialized = JSON.stringify(columns);
                const newSerialized = JSON.stringify(e.detail.columns);
                if (currentSerialized !== newSerialized) {
                    setColumns(e.detail.columns);
                }
            }
        };

        window.addEventListener('columnVisibilitySync', handleSync);
        return () => window.removeEventListener('columnVisibilitySync', handleSync);
    }, [storageKey, columns]);

    // Sync new default columns that might have been added to the codebase
    // after the user already cached their old config in localStorage.
    useEffect(() => {
        setColumns(prev => {
            let changed = false;
            const updated = defaultColumns.map(defCol => {
                const existing = prev.find(c => c.id === defCol.id);
                if (!existing) {
                    changed = true;
                    return defCol; // new column found in codebase
                }
                return existing;
            });
            return changed ? updated : prev;
        });
    }, [defaultColumns]);

    // Toggle column visibility
    const toggleColumn = useCallback((id: string) => {
        setColumns((prev) => prev.map((col) => (col.id === id ? { ...col, visible: !col.visible } : col)))
    }, [])

    // Check if column is visible
    const isVisible = useCallback(
        (id: string): boolean => {
            return columns.find((c) => c.id === id)?.visible ?? true
        },
        [columns],
    )

    return [columns, toggleColumn, isVisible]
}
